import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  FormGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Divider,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Undo as UndoIcon,
  Notifications as BellIcon,
  Forum as ChatIcon,
  MarkEmailRead as DigestIcon,
  Email as EmailIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { usePermission } from '../../hooks/usePermission';

const EVENT_TYPES = [
  { key: 'critical_detection', label: 'Critical detections' },
  { key: 'sla_breach', label: 'SLA breaches' },
  { key: 'case_created', label: 'Case created' },
  { key: 'ingestion_summary', label: 'Ingestion summaries' },
];

const EMAIL_EVENTS = [
  { key: 'critical_detection', label: 'Critical detections' },
  { key: 'sla_breach', label: 'SLA breaches' },
  { key: 'case_assigned', label: 'Case assigned to me' },
  { key: 'detection_assigned', label: 'Detection assigned to me' },
  { key: 'task_assigned', label: 'Task assigned to me' },
  { key: 'automation', label: 'Automation rule notifications' },
  { key: 'case_comment', label: 'Comments on my cases' },
  { key: 'detection_comment', label: 'Comments on my detections' },
];

const DEFAULT_EMAIL_EVENTS = ['critical_detection', 'sla_breach', 'case_assigned', 'detection_assigned', 'task_assigned'];

const IN_APP = [
  { key: 'criticalDetections', label: 'Critical detections', help: 'Alert managers when a critical detection is created' },
  { key: 'assignments', label: 'Assignments', help: 'Notify users when a detection, case or task is assigned to them' },
  { key: 'comments', label: 'Comments', help: 'Notify assignees when someone comments on their detection or case' },
  { key: 'slaBreaches', label: 'SLA breaches', help: 'Notify assignees and managers when an SLA target is missed' },
];

const emptyChannel = () => ({ enabled: false, webhookUrl: '', events: [] });

const normalise = (n = {}) => ({
  slack: { ...emptyChannel(), ...(n.slack || {}), events: Array.isArray(n.slack?.events) ? n.slack.events : [] },
  teams: { ...emptyChannel(), ...(n.teams || {}), events: Array.isArray(n.teams?.events) ? n.teams.events : [] },
  inApp: {
    criticalDetections: n.inApp?.criticalDetections !== false,
    assignments: n.inApp?.assignments !== false,
    comments: n.inApp?.comments !== false,
    slaBreaches: n.inApp?.slaBreaches !== false,
  },
  email: {
    enabled: n.email?.enabled !== false,
    events: Array.isArray(n.email?.events) ? n.email.events : DEFAULT_EMAIL_EVENTS,
    dailyDigest: {
      enabled: n.email?.dailyDigest?.enabled !== false,
      hourUtc: Number.isInteger(n.email?.dailyDigest?.hourUtc) ? n.email.dailyDigest.hourUtc : 12,
      lastSentDate: n.email?.dailyDigest?.lastSentDate || null,
    },
  },
});

const mask = (url) => (url ? `••••••••••••${String(url).slice(-6)}` : '');
const serverMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const WebhookChannel = ({ kind, title, subheader, channel, editing, onChange, onEdit, onCancelEdit, canManage, onTest, testing, testResult }) => {
  const hasStored = !!channel.webhookUrl && !editing;
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardHeader
        avatar={<ChatIcon color="primary" />}
        title={title}
        subheader={subheader}
        action={
          <FormControlLabel
            control={<Switch checked={!!channel.enabled} onChange={(e) => onChange({ enabled: e.target.checked })} disabled={!canManage} />}
            label={channel.enabled ? 'Enabled' : 'Disabled'}
          />
        }
      />
      <CardContent>
        <TextField
          fullWidth
          label="Incoming webhook URL"
          placeholder="https://..."
          value={hasStored ? mask(channel.webhookUrl) : (editing ? channel.draftUrl : '')}
          onChange={(e) => onChange({ draftUrl: e.target.value })}
          disabled={!canManage || hasStored}
          helperText={hasStored ? 'A webhook URL is stored server-side. Click the pencil to replace it.' : 'Paste the https webhook URL from Slack or Teams.'}
          InputProps={{
            endAdornment: canManage ? (
              <InputAdornment position="end">
                {hasStored ? (
                  <Tooltip title="Replace URL"><IconButton size="small" onClick={onEdit}><EditIcon fontSize="small" /></IconButton></Tooltip>
                ) : channel.webhookUrl ? (
                  <Tooltip title="Keep stored URL"><IconButton size="small" onClick={onCancelEdit}><UndoIcon fontSize="small" /></IconButton></Tooltip>
                ) : null}
              </InputAdornment>
            ) : null,
          }}
        />
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Events</Typography>
        <Typography variant="caption" color="text.secondary">Leave all unchecked to receive every event.</Typography>
        <FormGroup row>
          {EVENT_TYPES.map((ev) => (
            <FormControlLabel
              key={ev.key}
              control={
                <Checkbox
                  size="small"
                  checked={channel.events.includes(ev.key)}
                  disabled={!canManage}
                  onChange={(e) => onChange({ events: e.target.checked ? [...channel.events, ev.key] : channel.events.filter((x) => x !== ev.key) })}
                />
              }
              label={ev.label}
            />
          ))}
        </FormGroup>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={testing ? <CircularProgress size={14} /> : <SendIcon />}
            disabled={!canManage || testing || (!editing && !channel.webhookUrl) || (editing && !channel.draftUrl)}
            onClick={() => onTest(kind)}
          >
            Send test
          </Button>
          {testResult && (
            <Typography variant="caption" color={testResult.success ? 'success.main' : 'error.main'}>
              {testResult.message}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const NotificationSettings = () => {
  const { hasPermission, canManageCompany } = usePermission();
  const canRead = hasPermission('company', 'read');

  const [config, setConfig] = useState(null);
  const [editing, setEditing] = useState({ slack: false, teams: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [testing, setTesting] = useState({});
  const [testResult, setTestResult] = useState({});
  const [digestPreviewOpen, setDigestPreviewOpen] = useState(false);
  const [digestPreviewLoading, setDigestPreviewLoading] = useState(false);
  const [digestPreviewError, setDigestPreviewError] = useState(null);
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestTesting, setDigestTesting] = useState(false);
  const [digestTestResult, setDigestTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.settings.getCompany();
      const n = normalise(res.data?.notifications);
      setConfig({ ...n, slack: { ...n.slack, draftUrl: '' }, teams: { ...n.teams, draftUrl: '' } });
      setEditing({ slack: !n.slack.webhookUrl, teams: !n.teams.webhookUrl });
    } catch (e) {
      setError(serverMessage(e, 'Failed to load notification settings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (canRead) load(); else setLoading(false); }, [canRead, load]);

  const updateChannel = (kind, patch) => setConfig((c) => ({ ...c, [kind]: { ...c[kind], ...patch } }));
  const updateInApp = (key, value) => setConfig((c) => ({ ...c, inApp: { ...c.inApp, [key]: value } }));
  const updateDigest = (patch) => setConfig((c) => ({ ...c, email: { ...c.email, dailyDigest: { ...c.email.dailyDigest, ...patch } } }));
  const updateEmailEnabled = (enabled) => setConfig((c) => ({ ...c, email: { ...c.email, enabled } }));
  const toggleEmailEvent = (key, checked) => setConfig((c) => ({
    ...c,
    email: { ...c.email, events: checked ? [...c.email.events, key] : c.email.events.filter((x) => x !== key) },
  }));

  const effectiveUrl = (kind) => (editing[kind] ? config[kind].draftUrl : config[kind].webhookUrl);

  const handleTest = async (kind) => {
    const url = effectiveUrl(kind);
    if (!url) return;
    setTesting((t) => ({ ...t, [kind]: true }));
    setTestResult((r) => ({ ...r, [kind]: null }));
    try {
      const res = await apiService.workflows.testWebhook(kind, url);
      setTestResult((r) => ({ ...r, [kind]: { success: true, message: res.data?.message || 'Test notification delivered' } }));
    } catch (e) {
      setTestResult((r) => ({ ...r, [kind]: { success: false, message: serverMessage(e, 'Webhook call failed') } }));
    } finally {
      setTesting((t) => ({ ...t, [kind]: false }));
    }
  };

  const handleOpenDigestPreview = async () => {
    setDigestPreviewOpen(true);
    setDigestPreviewLoading(true);
    setDigestPreviewError(null);
    setDigestPreview(null);
    try {
      const res = await apiService.digest.preview();
      setDigestPreview(res.data);
    } catch (e) {
      setDigestPreviewError(serverMessage(e, 'Failed to load the digest preview'));
    } finally {
      setDigestPreviewLoading(false);
    }
  };

  const handleSendDigestTest = async () => {
    setDigestTesting(true);
    setDigestTestResult(null);
    try {
      const res = await apiService.digest.sendTest();
      if (res.data?.sent) {
        setDigestTestResult({ severity: 'success', message: `Sent to ${(res.data.recipients || []).join(', ') || 'recipients'}` });
      } else {
        setDigestTestResult({ severity: 'info', message: res.data?.skipped || 'The digest was not sent' });
      }
    } catch (e) {
      setDigestTestResult({ severity: 'error', message: serverMessage(e, 'Failed to send the test digest') });
    } finally {
      setDigestTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const channel = (kind) => {
        const c = config[kind];
        const out = { enabled: !!c.enabled, events: c.events };
        // Only send the URL when the user typed a new one; the backend deep-merges,
        // so leaving it out keeps the stored value.
        if (editing[kind] && c.draftUrl.trim()) out.webhookUrl = c.draftUrl.trim();
        else if (editing[kind] && !c.webhookUrl) out.webhookUrl = '';
        return out;
      };
      const payload = {
        notifications: {
          slack: channel('slack'),
          teams: channel('teams'),
          inApp: config.inApp,
          email: {
            enabled: !!config.email.enabled,
            events: config.email.events,
            dailyDigest: { enabled: !!config.email.dailyDigest.enabled, hourUtc: config.email.dailyDigest.hourUtc },
          },
        },
      };
      await apiService.settings.updateCompany(payload);
      setSuccess('Notification settings saved');
      await load();
    } catch (e) {
      setError(serverMessage(e, 'Failed to save notification settings'));
    } finally {
      setSaving(false);
    }
  };

  if (!canRead) {
    return <Alert severity="warning">You do not have permission to view notification settings.</Alert>;
  }
  if (loading || !config) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">Notifications</Typography>
          <Typography variant="body2" color="text.secondary">
            Choose which events create in-app notifications and forward important events to Slack or Microsoft Teams.
          </Typography>
        </Box>
        {canManageCompany && (
          <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={handleSave} disabled={saving}>
            Save
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {!canManageCompany && <Alert severity="info" sx={{ mb: 2 }}>You can view these settings but need the company:manage permission to change them.</Alert>}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardHeader avatar={<BellIcon color="primary" />} title="In-app notifications" subheader="Shown in the bell menu in the top bar" />
        <CardContent>
          <Grid container spacing={1}>
            {IN_APP.map((item) => (
              <Grid item xs={12} sm={6} key={item.key}>
                <FormControlLabel
                  control={<Switch checked={!!config.inApp[item.key]} disabled={!canManageCompany} onChange={(e) => updateInApp(item.key, e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="body2">{item.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.help}</Typography>
                    </Box>
                  }
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardHeader
          avatar={<EmailIcon color="primary" />}
          title="Email notifications"
          subheader="Send individual emails for important events, in addition to in-app notifications"
          action={
            <FormControlLabel
              control={
                <Switch
                  checked={!!config.email.enabled}
                  disabled={!canManageCompany}
                  onChange={(e) => updateEmailEnabled(e.target.checked)}
                />
              }
              label={config.email.enabled ? 'Enabled' : 'Disabled'}
            />
          }
        />
        <CardContent>
          <FormGroup>
            <Grid container spacing={1}>
              {EMAIL_EVENTS.map((ev) => (
                <Grid item xs={12} sm={6} key={ev.key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={config.email.events.includes(ev.key)}
                        disabled={!canManageCompany}
                        onChange={(e) => toggleEmailEvent(ev.key, e.target.checked)}
                      />
                    }
                    label={ev.label}
                  />
                </Grid>
              ))}
            </Grid>
          </FormGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Each user can still switch email off entirely in their own profile settings.
          </Typography>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardHeader
          avatar={<DigestIcon color="primary" />}
          title="Daily email digest"
          subheader="A morning summary of new detections, open cases, SLA breaches and highest-risk assets"
          action={
            <FormControlLabel
              control={
                <Switch
                  checked={!!config.email.dailyDigest.enabled}
                  disabled={!canManageCompany}
                  onChange={(e) => updateDigest({ enabled: e.target.checked })}
                />
              }
              label={config.email.dailyDigest.enabled ? 'Enabled' : 'Disabled'}
            />
          }
        />
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Send at (UTC hour)</InputLabel>
                <Select
                  label="Send at (UTC hour)"
                  value={config.email.dailyDigest.hourUtc}
                  disabled={!canManageCompany}
                  onChange={(e) => updateDigest({ hourUtc: Number(e.target.value) })}
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <MenuItem key={h} value={h}>{`${String(h).padStart(2, '0')}:00 UTC`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8}>
              <Typography variant="caption" color="text.secondary">
                {config.email.dailyDigest.lastSentDate
                  ? `Last sent: ${config.email.dailyDigest.lastSentDate}`
                  : 'Not sent yet.'}
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <Button variant="outlined" size="small" startIcon={<PreviewIcon />} onClick={handleOpenDigestPreview}>
              Preview
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={digestTesting ? <CircularProgress size={14} /> : <SendIcon />}
              disabled={digestTesting || !canManageCompany}
              onClick={handleSendDigestTest}
            >
              Send me a test
            </Button>
            {digestTestResult && (
              <Typography variant="caption" color={`${digestTestResult.severity}.main`}>
                {digestTestResult.message}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        {[
          ['slack', 'Slack', 'Incoming webhook (Slack app → Incoming Webhooks)'],
          ['teams', 'Microsoft Teams', 'Incoming webhook connector on a channel'],
        ].map(([kind, title, subheader]) => (
          <Grid item xs={12} md={6} key={kind}>
            <WebhookChannel
              kind={kind}
              title={title}
              subheader={subheader}
              channel={config[kind]}
              editing={editing[kind]}
              canManage={canManageCompany}
              onChange={(patch) => updateChannel(kind, patch)}
              onEdit={() => setEditing((e) => ({ ...e, [kind]: true }))}
              onCancelEdit={() => { setEditing((e) => ({ ...e, [kind]: false })); updateChannel(kind, { draftUrl: '' }); }}
              onTest={handleTest}
              testing={!!testing[kind]}
              testResult={testResult[kind]}
            />
          </Grid>
        ))}
      </Grid>

      <Dialog open={digestPreviewOpen} onClose={() => setDigestPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Daily digest preview</DialogTitle>
        <DialogContent>
          {digestPreviewLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          )}
          {digestPreviewError && <Alert severity="error">{digestPreviewError}</Alert>}
          {digestPreview && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {digestPreview.recipients?.length
                  ? `Would be sent to: ${digestPreview.recipients.join(', ')} (${digestPreview.recipients.length})`
                  : 'No recipients have opted in'}
              </Typography>
              <iframe
                srcDoc={digestPreview.html}
                sandbox=""
                title="Digest preview"
                style={{ width: '100%', height: '70vh', border: '1px solid #e0e0e0' }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDigestPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationSettings;
